/* eslint-disable sort-keys-fix/sort-keys-fix */
import Logo from './Logo';

// BASICS
export const baseColors = {
  black: '#000',
  gray: [
    '#fff', // 0 index
    '#f8f8f8',
    '#dedede',
    '#B0AEB0',
    '#979797',
    '#101213', // 5
  ],
  primary: '#D52A3A',
  primaryHover: '#F4F4F4',
  secondary: '#F4F4F4',
  secondaryHover: '#D52A3A',
  tertiary: '#4F4F4F',
  red: '#f00101',
  white: '#fff',
  yellow: '#ffcc32',
};

export const colors = {
  ...baseColors,
  background: baseColors.white,
  bodyCopy: baseColors.white, // Used by breadcrumb component
  danger: baseColors.red,
  dark: baseColors.gray[4],
  darkGray: baseColors.gray[5],
  info: baseColors.tertiary,
  primaryWhite: baseColors.white,
  light: baseColors.gray[2],
  lightGray: baseColors.gray[2],
  muted: baseColors.gray[1],
  success: baseColors.primary,
  text: baseColors.gray[5],
  textMuted: baseColors.gray[3],
  warning: baseColors.yellow,
};

export const fonts = {
  body: 'Encode Sans, sans-serif',
  heading: 'Encode Sans, sans-serif',
  monospace: 'Menlo, monospace',
};

export const fontWeights = {
  body: 400,
  bold: 700,
  display: 300,
  heading: 400,
  headingAlt: 600,
  light: 300,
  normal: 400,
};

export const fontSizes = {
  0: '0.75rem', // 12
  1: '0.875rem', // 14
  2: '1rem', // 16
  3: '1.0625rem', // 17
  4: '1.125rem', // 18
  5: '1.25rem', // 20
  6: '1.375rem', // 22
  7: '1.5rem', // 24
  8: '1.6875rem', // 27
  9: '1.875rem', // 30
  10: '2rem', // 32
  11: '2.1875rem', // 35
  12: '2.5rem', // 40
  13: '2.5625rem', // 41
  14: '2.875rem', // 46
  15: '3.5rem', // 56
};

export const lineHeights = {
  body: 1.55555,
  heading: 1.3,
  paragraph: 1.55555,
};

export const sizes = {
  lg: '1150px',
  md: '720px',
  sm: '540px',
  xl: '1440px',
};

export const radii = {
  default: '0.25rem',
  pill: '50rem',
  lg: '0.3rem',
  sm: '0.2rem',
};

export const shadows = {
  default: '0 .5rem 1rem rgba(0, 0, 0, .15)',
  lg: '0 1rem 3rem rgba(0, 0, 0, .175)',
  sm: '0 .125rem .25rem rgba(0, 0, 0, .075)',
};

export const text = {
  caps: {
    letterSpacing: '0.2em',
    textTransform: 'uppercase',
  },
  heading: {
    fontFamily: fonts.heading,
    fontWeight: fontWeights.heading,
    lineHeight: lineHeights.heading,
    color: colors.primary,
  },
};

const heading = {
  fontFamily: fonts.heading,
  fontWeight: fontWeights.heading,
  color: text.heading.color,
};

const display = {
  fontWeight: fontWeights.display,
  lineHeight: lineHeights.heading,
};

// variants
const typeStyles = {
  display,
  heading,
};

export const styles = {
  a: {
    '&:hover': {
      color: colors.primaryHover,
    },
    color: colors.primary,
    textDecoration: 'underline',
  },
  blockquote: {
    backgroundColor: colors.primary,
    color: colors.white,
    fontStyle: 'normal',
    fontWeight: 'bold',
    textTransform: 'normal',
  },
  h1: {
    ...heading,
    fontSize: { desktop: fontSizes[15], tablet: fontSizes[14], mobile: fontSizes[13] },
    lineHeight: { desktop: '4.0625rem', tablet: '3.125rem', mobile: '2.5rem' },
  },
  h2: {
    ...heading,
    fontSize: { desktop: fontSizes[12], tablet: fontSizes[11], mobile: fontSizes[9] },
    lineHeight: { desktop: '2.6875rem', tablet: '2.375rem', mobile: '2.25rem' },
  },
  h3: {
    ...heading,
    fontSize: { desktop: fontSizes[10], tablet: fontSizes[8], mobile: fontSizes[6] },
    lineHeight: { desktop: '2.1875rem', tablet: '1.875rem', mobile: '1.625rem' },
  },
  h4: {
    ...heading,
    fontSize: { desktop: fontSizes[7], tablet: fontSizes[5], mobile: fontSizes[4] },
    lineHeight: { desktop: '1.5rem', tablet: '1.375rem', mobile: '1.375rem' },
  },
  h5: {
    ...heading,
    fontSize: { desktop: fontSizes[5], tablet: fontSizes[4], mobile: fontSizes[3] },
    lineHeight: { desktop: '1.25rem', tablet: '1.25rem', mobile: '1.25rem' },
  },
  highlight: {
    backgroundColor: colors.primary,
    color: colors.white,
  },
  img: {
    height: 'auto',
    maxWidth: '100%',
  },
  inlineCode: {
    color: colors.darkGray,
  },
  li: {
    bulletBorderRadius: '10px',
    bulletColor: colors.secondary,
  },
  p: {
    fontSize: { desktop: fontSizes[3], mobile: fontSizes[2], tablet: fontSizes[3] },
    lineHeight: { desktop: '1.75rem', mobile: '1.625rem', tablet: '1.75rem' },
    margin: '0',
    marginBottom: '1.7rem',
  },
  root: {
    backgroundColor: colors.white,
    fontFamily: fonts.body,
    fontWeight: fontWeights.body,
    lineHeight: lineHeights.body,
  },
  rteHighlight: {
    color: colors.primary,
    hoverColor: colors.primaryHover,
    textDecoration: 'underline',
    isFooter: colors.white,
  },
  table: {
    borderCollapse: 'collapse',
    color: colors.gray[3],
    marginBottom: '3rem',
    width: '100%',
  },
  td: {
    borderBottomColor: colors.gray[3],
    borderBottomStyle: 'solid',
    borderBottomWidth: 2,
    padding: '.75rem',
    verticalAlign: 'top',
  },
  th: {
    borderBottomColor: colors.gray[3],
    borderBottomStyle: 'solid',
    borderBottomWidth: '2px',
    borderTopColor: colors.gray[3],
    borderTopStyle: 'solid',
    borderTopWidth: '2px',
    padding: '.75rem',
    textAlign: 'inherit',
    verticalAlign: 'bottom',
  },
};

const buttonDefaults = {
  borderRadius: '30px',
  cursor: 'pointer',
  fontFamily: fonts.body,
  fontSize: fontSizes[3],
  fontWeight: 600,
  height: 'auto',
  padding: '15px 25px',
  lineHeight: 1,
};

export const buttons = {
  primary: {
    backgroundColor: colors.primary,
    color: colors.white,
    borderRadius: '30px',
    border: '2px solid transparent',
    fontWeight: 600,
    '&:hover': {
      backgroundColor: colors.primaryHover,
      color: colors.primary,
    },
  },
  secondary: {
    backgroundColor: colors.secondary,
    color: colors.primary,
    borderRadius: '30px',
    fontWeight: 600,
    border: `2px solid ${colors.primary}`,
    '&:hover': {
      backgroundColor: colors.secondaryHover,
      color: colors.secondary,
      border: `2px solid ${colors.secondary}`,
    },
  },
  disabled: {
    ...buttonDefaults,
    backgroundColor: colors.muted,
    color: colors.textMuted,
    cursor: 'default',
  },
  'primary-with-background': {
    ...buttonDefaults,
    '&:hover': {
      backgroundColor: colors.primary,
      color: colors.primaryHover,
    },
    backgroundColor: colors.white,
    borderColor: colors.white,
    borderStyle: 'solid',
    borderWidth: '2px',
    color: colors.primary,
  },
  tertiary: {
    ...buttonDefaults,
    '&:hover': {
      backgroundColor: colors.secondaryHover,
      borderColor: colors.secondaryHover,
      color: colors.white,
    },
    backgroundColor: colors.white,
    borderColor: colors.secondary,
    borderStyle: 'solid',
    borderWidth: '1px',
    color: colors.primary,
  },
};

// COMPONENTS
export const adviceCardModule = {
  backgroundColor: colors.primary,
};

export const articleDetail = {
  introduction: colors.text,
  tags: {
    backgroundColor: colors.primary,
    color: colors.tertiary,
  },
  title: colors.primary,
};

export const articleListing = {
  background: colors.primary,
  filter: colors.text,
  hover: colors.secondary,
  mobile: {
    background: colors.tertiary,
    title: colors.primary,
    text: colors.text,
  },
  title: colors.primary,
  text: colors.white,
};

export const breadcrumb = {
  backgroundColor: colors.tertiary,
  highlight: colors.white, // Used for highlights and chevrons
  color: colors.white, // For backwards compatibility
};

export const cardCtas = {
  arrowColor: colors.primary,
  arrowHoverColor: colors.primary,
  color: colors.primary,
  fontSize: '0.75rem',
  hoverColor: colors.primary,
  letterSpacing: '2px',
  lineHeight: '13px',
  textTransform: 'uppercase',
};

export const cards = {
  article: {
    description: colors.text,
    link: {
      '&:hover': {
        color: colors.secondary,
        opacity: 1,
        textDecoration: 'underline',
      },
      color: colors.primary,
    },
    title: colors.primary,
  },
  logo: {
    color: colors.text,
  },
};

export const carousel = {
  controls: {
    hero: {
      backgroundColor: colors.white,
      stroke: colors.white,
      color: colors.secondary,
      opacity: '0.8',
      hover: {
        backgroundColor: colors.secondary,
        color: colors.white,
        stroke: colors.secondary,
      },
    },
    regular: {
      backgroundColor: colors.white,
      color: colors.primary,
      hover: {
        color: colors.secondary,
        backgroundColor: colors.tertiary,
      },
    },
  },
  indicator: colors.darkGray,
  link: {
    '&:hover': {
      color: colors.secondary,
      opacity: 1,
      textDecoration: 'underline',
    },
    color: colors.primary,
  },
  text: colors.text,
};

export const categoryCards = {
  backgroundColor: colors.primary,
};

export const categorySection = {
  background: colors.primary,
  boxShadow: null,
  chevron: {
    fill: colors.primary,
    stroke: colors.white,
  },
  color: colors.white,
  date: colors.tertiary,
  dateColor: colors.text,
  headingColor: colors.secondary,
};

export const columns = {
  imageCard: colors.text,
  imageCardTitle: colors.text,
  infoBlock: {
    background: colors.primary,

    button: {
      backgroundColor: colors.white,
      color: colors.primary,
      borderRadius: '24px',
      '&:hover': {
        backgroundColor: colors.tertiary + ' !important',
        color: colors.white,
        textDecoration: 'none',
      },
    },
    description: colors.white,
    title: colors.white,
  },
};
export const contactForm = {
  heading: {
    color: colors.secondary,
  },
};

export const contacts = {
  office: {
    mapTextColor: colors.primary,
    mapTextFontSize: '0.875rem',
    mapTextFontWeight: 'normal',
    mapTextLetterSpacing: '2.33px',
    mapTextLineHeight: '1.71',
    mapTextTransform: 'uppercase',
    openingHoursFontSize: '1rem',
  },
};

export const cookieBanner = {
  backgroundColor: colors.primary,
};

export const ctaBanner = {
  focusImage: {
    backgroundColor: colors.primary,
    color: colors.white,
    titleColor: colors.white, // Add explicit titleColor for the title
  },
  focusText: {
    backgroundColor: colors.primary,
    color: colors.white,
    titleColor: colors.white, // Add explicit titleColor for the title
    titleFontWeight: 400,
  },
};

export const ctaGroup = {
  title: colors.white,
  fontWeight: 400,
  backgroundColor: colors.primary,
  chevron: {
    opacity: 1,
    stroke: '#000',
  },
  borderRadius: 0,
};

export const ctaGroupButton = {
  backgroundColor: colors.primary,
  color: colors.white,
  fontWeight: 400,
};

export const customBlockCards = {
  backgroundColor: colors.primary,
};

export const dealer = {
  textColor: colors.primary,
};

export const documentDownload = {
  arrowColor: colors.secondary,
  arrowHoverColor: colors.secondaryHover,
  color: colors.secondary,
  hoverColor: colors.secondaryHover,
};

export const footer = {
  activeColor: colors.gray[2],
  backgroundColor: colors.white,
  color: colors.white,
  minHeight: '160px',
};

export const formInputs = {
  activeColor: colors.primary,
  borderRadius: '24px',
  radioInputBorderRadius: '50%',
  radioSpanBorderRadius: '50%',
};

export const header = {
  activeColor: colors.primary,
  backgroundColor: colors.white,
  color: colors.darkGray,
};

export const headerBottom = {
  activeFontWeight: 'bold',
  backgroundColor: colors.tertiary,
  fontSize: '12px',
  fontWeight: 'normal',
};

export const heroBannerButton = {
  'No Background': {
    '&:hover': {
      backgroundColor: colors.white,
      borderColor: colors.white,
      color: colors.white,
    },
    backgroundColor: colors.white,
    borderColor: colors.white,
    borderRadius: '30px',
    borderStyle: 'solid',
    borderWidth: '2px',
    color: colors.white,
    height: '42px',
    justifyContent: 'center',
  },
  'White Background': {
    '&:hover': {
      backgroundColor: colors.primaryHover,
      border: 'none',
      color: colors.white,
    },
    backgroundColor: colors.primary,
    color: colors.white,
    borderRadius: '30px',
    justifyContent: 'center',
    minHeight: '42px',
    padding: '13px 35px 12px',
  },
  arrow: {
    display: 'none',
  },
};

export const heroSection = {
  titleFontWeight: 'normal',
  backgroundColor: colors.tertiary,
};

export const imageAndText = {
  color: colors.secondary,
};

export const infoSection = {
  backgroundColor: colors.primary,
  color: colors.white,
  title: colors.white,
};

export const infoBlock = {
  background: colors.tertiary,
  button: {
    '&:hover': {
      backgroundColor: colors.secondary,
      textDecoration: 'none',
    },
    backgroundColor: colors.primary,
    color: colors.white,
  },
  description: colors.text,
  title: colors.text,
};

export const ingredients = {
  backgroundColor: colors.primary,
  color: colors.white,
  hoverColor: colors.secondary,
};

export const media = {
  backgroundColor: colors.primary,
  titleFontWeight: 'bold',
  titleTextTransform: 'normal',
};

export const mobileNavigation = {
  arrowColor: colors.primary,
  chevronColor: colors.primary,
};

export const navItems = {
  fontSize: '0.75rem',
};
export const newsListing = {
  titleFontWeight: 'normal',
};

export const ownerDetail = {
  introduction: colors.text,
  tags: {
    backgroundColor: colors.primary,
    color: colors.tertiary,
  },
  title: colors.primary,
};

export const product = {
  backgroundColor: colors.secondary,
  color: colors.white,
};

export const productListing = {
  titleFontWeight: 'normal',
};

export const salesPerson = {
  borderColor: colors.primary,
};

export const search = {
  activeColor: colors.primary,
  borderRadius: '20px',
};

export const shortBanner = {
  backgroundColor: colors.primary,
  color: colors.white,
  titleColor: colors.white,
  titleFontWeight: 'bold',
  titleTextTransform: 'none',
};

export const table = {
  headerBackgroundColor: colors.primary,
  headerColor: colors.white,
  scrollbarColor: colors.secondary,
};

export const upComingEvents = {
  backgroundColor: colors.primary,
  color: colors.white,
};

export default {
  adviceCardModule,
  articleDetail,
  articleListing,
  breadcrumb,
  buttons,
  cardCtas,
  cards,
  carousel,
  categoryCards,
  categorySection,
  colors,
  columns,
  contactForm,
  contacts,
  cookieBanner,
  ctaBanner,
  ctaGroup,
  customBlockCards,
  dealer,
  documentDownload,
  fonts,
  fontSizes,
  fontWeights,
  footer,
  formInputs,
  header,
  headerBottom,
  heroBannerButton,
  heroSection,
  imageAndText,
  infoBlock,
  infoSection,
  ingredients,
  lineHeights,
  logo: Logo,
  media,
  mobileNavigation,
  name: 'Vaesken',
  navItems,
  newsListing,
  ownerDetail,
  product,
  productListing,
  radii,
  salesPerson,
  search,
  shadows,
  shortBanner,
  sizes,
  styles,
  table,
  text,
  typeStyles,
  upComingEvents,
};
